/**
 * Handler for adding a nested mission (subtask as mission).
 * 
 * Creates mission inside nested campaign.
 * If task doesn't have a nested campaign, creates one first.
 * Works for tasks in any sprint (current or nested).
 */

const LOG_PREFIX = '[useCrossSprintActions]';

/**
 * Creates a new nested sprint for a task.
 */
const createNestedSprint = (task, parentSprint, nestedSprintId) => {
  const sprintName = task.name && task.name.length > 35
    ? task.name.slice(0, 35) + '...'
    : (task.name || 'Untitled');

  return {
    id: nestedSprintId,
    name: sprintName,
    icon: task.icon || null,
    ownerId: parentSprint.ownerId,
    ownerName: parentSprint.ownerName,
    status: 'active',
    progress: 0,
    createdAt: new Date().toISOString(),
    parentTaskId: task.id,
    parentSprintId: parentSprint.id,
    tasks: [],
  };
};

/**
 * Creates a new mission inside a nested sprint.
 */
const createMission = (missionName, existingTasksCount) => {
  const now = new Date().toISOString();
  return {
    id: `task-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: missionName.trim(),
    order: existingTasksCount + 1,
    dependsOn: [],
    status: 'pending',
    x: 100 + (existingTasksCount * 200),
    y: 100,
    createdAt: now,
    updatedAt: now,
  };
};

/**
 * Creates the add subtask as mission handler.
 */
export const createMissionHandler = ({
  findSprintForTask,
  allSprints,
  onAddSprint,
  onUpdateSprint,
}) => (taskId) => {
  const { sprint: parentSprint, isCurrentSprint } = findSprintForTask(taskId);

  if (!parentSprint) {
    console.warn(`${LOG_PREFIX} handleAddSubtaskAsMission: Task not found in any sprint:`, taskId);
    return;
  }

  const task = parentSprint.tasks?.find(t => t.id === taskId);
  if (!task) {
    console.warn(`${LOG_PREFIX} handleAddSubtaskAsMission: Task not found in sprint:`, taskId);
    return;
  }

  // Prompt for mission name
  const missionName = prompt('Enter mission name:');
  if (!missionName || !missionName.trim()) return;

  // Check if task already has a nested sprint
  let nestedSprintId = task.nestedSprintId;
  let nestedSprint = nestedSprintId ? allSprints.find(s => s.id === nestedSprintId) : null;

  if (!nestedSprint) {
    // Create a new nested sprint for this task
    nestedSprintId = `nested-sprint-${Date.now()}`;
    nestedSprint = createNestedSprint(task, parentSprint, nestedSprintId);
    
    // Parent mission link first, then persist the nested campaign.
    const updatedParentSprint = {
      ...parentSprint,
      tasks: parentSprint.tasks.map(t =>
        t.id === taskId ? { ...t, nestedSprintId, icon: null } : t
      ),
    };

    onUpdateSprint?.(updatedParentSprint);
    onAddSprint?.(nestedSprint);
  }

  // Create the new mission
  const newMission = createMission(missionName, nestedSprint.tasks?.length || 0);

  // Update the nested sprint with the new mission
  const updatedNestedSprint = {
    ...nestedSprint,
    tasks: [...(nestedSprint.tasks || []), newMission],
  };

  onUpdateSprint?.(updatedNestedSprint);

  console.log(`${LOG_PREFIX} Added nested mission:`, {
    taskId,
    parentSprintId: parentSprint.id,
    nestedSprintId,
    missionName: missionName.trim(),
    missionId: newMission.id,
  });
};
